// Redux
import { AsyncResult, Action, ApiAction, Result } from '..';
import api from '../redux-api';
import { startLoaderAction, stopLoaderAction } from './ui/loaders';

// Utils
import { calculateCorrelateRiskProfile } from './correlate-risk-profiles';
import { calculateRiskProfile } from '@/utils/risk-profiles';
import { deepClone } from '@/utils/objects';

// Types
import { DependentsConfigSchema, CompareStrategy } from '@/types/question-item';
import { KycQuestionnaireState } from './types/kyc-questionnaire';
import QUESTION_TYPES from '@/types/question-types';
import KycQuestionnaire from '@/types/kyc-questionnaire';
import QuestionOptionItem from '@/types/question-option-item';
import RiskProfileItem, { SelectedRiskProfile } from '@/types/risk-profile-item';
import QuestionnaireSectionItem from '@/types/questionnaire-section-item';
import QuestionItem from '@/types/question-item';
import ContactRiskProfile from '@/types/contact-risk-profile';
import SavedQuestions from '@/types/saved-questions';
import SavedSections from '@/types/saved-sections';
import { TypeOfModification } from '@/types/question-item';

import {
  calculateQuestionTotalValue,
  selectTargetQuestionOption,
  updateQuestionValueMapping,
  getSectionByQuestionId,
  getQuestionById,
} from '@/utils/questionnaire';

const INITIAL_STATE: KycQuestionnaireState = {
  questionnaires: [],
  newRiskProfile: {
    savedSections: {},
  },
  recommendedRiskProfile: undefined,
  isCompleted: false,
};

// Actions
export const GET_QUESTIONNAIRE: ApiAction = {
  type: 'connect/questionnaire/get-kyc-questionnaires',
  endpoint: 'kyc-questionnaires/',
};

const LOAD_QUESTIONNAIRE: Action = {
  type: 'connect/questionnaire/load-questionnaire',
};

const RESET_RISK_PROFILE: Action = {
  type: 'connect/questionnaire/reset',
};

const SET_RISK_PROFILE_SECTIONS: Action = {
  type: 'connect/questionnaire/set-sections',
};

const SET_RISK_PROFILE: Action = {
  type: 'connect/questionnaire/set-profile',
};

const SET_RISK_PROFILE_SCORE: Action = {
  type: 'connect/questionnaire/set-score',
};

const SET_RISK_PROFILE_FOR_EDIT: Action = {
  type: 'connect/questionnaire/set-profile-edit',
};

const SET_RECOMMENDED_RISK_PROFILE: Action = {
  type: 'connect/questionnaire/set-recommended-profile',
};

const COMPLETE_QUESTIONNAIRE: Action = {
  type: 'connect/questionnaire/complete-questionnaire',
};

const GO_BACK_QUESTIONNAIRE: Action = {
  type: 'connect/questionnaire/go-back-questionnaire',
};

export const SET_IS_RESETTING_RISK_PROFILE: Action = {
  type: 'connect/questionnaire/set-is-resetting-risk-profile',
};

// Reducer
export default function reducer(state: KycQuestionnaireState = INITIAL_STATE, action: Action): KycQuestionnaireState {
  switch (action.type) {
    case `${GET_QUESTIONNAIRE.type}_SUCCESS`: {
      return {
        ...state,
        questionnaires: action.data.data,
      };
    }
    case LOAD_QUESTIONNAIRE.type: {
      return {
        ...state,
        currentQuestionnaire: action.questionnaire,
        newRiskProfile: {
          ...state.newRiskProfile,
          questionnaire: action.questionnaire._id,
        },
      };
    }
    case RESET_RISK_PROFILE.type: {
      return {
        ...state,
        newRiskProfile: INITIAL_STATE.newRiskProfile,
        recommendedRiskProfile: INITIAL_STATE.recommendedRiskProfile,
        isCompleted: false,
      };
    }
    case SET_RISK_PROFILE_SECTIONS.type: {
      return {
        ...state,
        newRiskProfile: {
          ...state.newRiskProfile,
          savedSections: action.savedSections,
        },
      };
    }
    case SET_RISK_PROFILE.type: {
      return {
        ...state,
        newRiskProfile: {
          ...state.newRiskProfile,
          profile: action.profile,
        },
      };
    }
    case SET_RECOMMENDED_RISK_PROFILE.type: {
      return {
        ...state,
        recommendedRiskProfile: action.profile,
      };
    }
    case SET_RISK_PROFILE_SCORE.type: {
      return {
        ...state,
        newRiskProfile: {
          ...state.newRiskProfile,
          score: action.score,
        },
      };
    }
    case SET_RISK_PROFILE_FOR_EDIT.type: {
      return {
        ...state,
        newRiskProfile: action.profile,
      };
    }
    case COMPLETE_QUESTIONNAIRE.type: {
      return {
        ...state,
        isCompleted: true,
      };
    }
    case GO_BACK_QUESTIONNAIRE.type: {
      return {
        ...state,
        isCompleted: false,
      };
    }
    default: {
      return state;
    }
  }
}

export const getKycQuestionnaires = (): AsyncResult<KycQuestionnaire[]> => () =>
  api.get(GET_QUESTIONNAIRE).then((res: { questionnaires: KycQuestionnaire[] }) => res.questionnaires);

export const loadKycQuestionnaire =
  (questionnaire: KycQuestionnaire): Result<void> =>
  dispatch => {
    dispatch({ ...LOAD_QUESTIONNAIRE, questionnaire });
  };

export const resetRiskProfile = (): Result<void> => dispatch => {
  dispatch(startLoaderAction(SET_IS_RESETTING_RISK_PROFILE.type));
  dispatch(RESET_RISK_PROFILE);
  dispatch(stopLoaderAction(SET_IS_RESETTING_RISK_PROFILE.type));
};

export const isOverrideOptionInClonedSection = (clonedSections: SavedSections, overrideOptionId: string): boolean =>
  Object.keys(clonedSections).some(sectionId => {
    const section = clonedSections[sectionId];
    return Object.keys(section.questions).some(questionId => {
      const question = section.questions[questionId];
      return question.options.includes(overrideOptionId);
    });
  });

export const applyMultipleOverrides = (clonedSections: SavedSections, currentQuestionnaire: KycQuestionnaire, text?: string) => {
  const questionsWithMultipleOverrideOptions = currentQuestionnaire?.sections.flatMap(section =>
    Object.values(section.questions).filter(question => question.overrideValues && question.overrideValues.length > 0),
  );

  if (!questionsWithMultipleOverrideOptions || questionsWithMultipleOverrideOptions.length === 0) {
    return;
  }

  questionsWithMultipleOverrideOptions.forEach(question => {
    question.overrideValues.forEach((overrideValue: any) => {
      const { sourceOptionId, sourceQuestionId, sourceSectionId, targetOptionId, targetQuestionId, targetSectionId } = overrideValue;
      const isOverrideOptionSelected = clonedSections[sourceSectionId]?.questions[sourceQuestionId]?.options.includes(sourceOptionId);

      if (isOverrideOptionSelected) {
        if (!clonedSections[targetSectionId]) {
          clonedSections[targetSectionId] = { questions: {} };
        }

        if (!clonedSections[targetSectionId].questions) {
          clonedSections[targetSectionId].questions = {};
        }

        const value = question.options.find(o => o._id === targetOptionId)?.value || 0;
        clonedSections[targetSectionId].questions[targetQuestionId] = {
          options: [targetOptionId],
          value,
          text,
          externalScores: undefined,
        };
      }
    });
  });
};

export const applyCompareModifications = (clonedSections: SavedSections, currentQuestionnaire: KycQuestionnaire, text?: string) => {
  const questionsWithCompareDependents = currentQuestionnaire?.sections.flatMap(section =>
    section.questions.filter(question => question.dependents?.some(dep => dep.typeOfModification === TypeOfModification.COMPARE)),
  );

  if (!questionsWithCompareDependents || questionsWithCompareDependents.length === 0) {
    return;
  }

  // Group by dependent question ID to find all sources that feed into the same target
  const compareTargets: Record<string, Array<{ sourceQuestion: QuestionItem; sourceSectionId: string; compareStrategy: CompareStrategy }>> = {};

  questionsWithCompareDependents.forEach(sourceQuestion => {
    const sourceSection = currentQuestionnaire.sections.find(s => s.questions.some(q => q._id === sourceQuestion._id));
    if (!sourceSection) return;

    sourceQuestion.dependents?.forEach(dependent => {
      if (dependent.typeOfModification === TypeOfModification.COMPARE) {
        if (!compareTargets[dependent.dependentQuestionId]) {
          compareTargets[dependent.dependentQuestionId] = [];
        }
        compareTargets[dependent.dependentQuestionId].push({
          sourceQuestion,
          sourceSectionId: sourceSection._id,
          compareStrategy: dependent.compareStrategy ?? CompareStrategy.MIN, // Default to MIN for backward compatibility
        });
      }
    });
  });

  // Get all overrideValues to map source options to target options
  const overrideValuesMappings = currentQuestionnaire?.sections.flatMap(section =>
    section.questions.flatMap(question => {
      if (question.overrideValues && question.overrideValues.length > 0) {
        return question.overrideValues.map((ov: any) => ({
          ...ov,
          targetQuestion: question,
        }));
      }
      return [];
    }),
  );

  // Process each target question that has COMPARE sources
  Object.keys(compareTargets).forEach(targetQuestionId => {
    const sources = compareTargets[targetQuestionId];
    const targetSection = getSectionByQuestionId(currentQuestionnaire, targetQuestionId);
    const targetQuestion = getQuestionById(targetSection, targetQuestionId);

    if (!targetSection || !targetQuestion) {
      return;
    }

    // Collect target option IDs and their values for all selected source options
    const targetOptionsFromSources: Array<{ targetOptionId: string; optionValue: number }> = [];

    sources.forEach(({ sourceQuestion, sourceSectionId }) => {
      const savedSourceQuestion = clonedSections[sourceSectionId]?.questions[sourceQuestion._id];
      if (savedSourceQuestion && savedSourceQuestion.options.length > 0) {
        const selectedSourceOptionId = savedSourceQuestion.options[0];

        // Find the override mapping that maps this source option to a target option
        const mapping = overrideValuesMappings.find(
          (ov: any) => ov.sourceQuestionId === sourceQuestion._id && ov.sourceOptionId === selectedSourceOptionId && ov.targetQuestionId === targetQuestionId,
        );

        if (mapping) {
          const { targetOptionId } = mapping;
          const targetOption = targetQuestion.options.find(opt => opt._id === targetOptionId);
          if (targetOption) {
            targetOptionsFromSources.push({
              targetOptionId,
              optionValue: targetOption.value ?? 0,
            });
          }
        }
      }
    });

    // Check if target already has a value from a non-COMPARE source (forced override)
    const existingTargetQuestion = clonedSections[targetSection._id]?.questions[targetQuestionId];
    if (existingTargetQuestion && existingTargetQuestion.options.length > 0) {
      const existingOptionId = existingTargetQuestion.options[0];
      const existingOption = targetQuestion.options.find(opt => opt._id === existingOptionId);

      if (existingOption) {
        const isFromCompareSource = sources.some(({ sourceQuestion, sourceSectionId }) => {
          const savedSourceQuestion = clonedSections[sourceSectionId]?.questions[sourceQuestion._id];
          if (!savedSourceQuestion) return false;

          const mapping = overrideValuesMappings.find(
            (ov: any) =>
              ov.sourceQuestionId === sourceQuestion._id &&
              ov.sourceOptionId === savedSourceQuestion.options[0] &&
              ov.targetQuestionId === targetQuestionId &&
              ov.targetOptionId === existingOptionId,
          );
          return !!mapping;
        });

        // Only include existing value if it's NOT from a COMPARE source (i.e., it's a forced override)
        if (!isFromCompareSource) {
          targetOptionsFromSources.push({
            targetOptionId: existingOptionId,
            optionValue: existingOption.value ?? 0,
          });
        }
      }
    }

    if (targetOptionsFromSources.length === 0) {
      return;
    }

    // Determine the comparison strategy - check if all sources have the same strategy
    // If strategies conflict, fallback to MIN (most conservative) for safety
    const strategies = sources.map(s => s.compareStrategy ?? CompareStrategy.MIN);
    const allSameStrategy = strategies.every(s => s === strategies[0]);
    const strategy = allSameStrategy ? strategies[0] : CompareStrategy.MIN;

    // Select option based on strategy: MIN = most conservative (lowest value), MAX = least conservative (highest value)
    targetOptionsFromSources.sort((a, b) => (strategy === CompareStrategy.MIN ? a.optionValue - b.optionValue : b.optionValue - a.optionValue));
    const selectedOption = targetOptionsFromSources[0];

    if (!(targetSection._id in clonedSections)) {
      clonedSections[targetSection._id] = { questions: {} };
    }

    const optionValue = targetQuestion.options.find(opt => opt._id === selectedOption.targetOptionId)?.value || 0;

    clonedSections[targetSection._id].questions[targetQuestionId] = {
      options: [selectedOption.targetOptionId],
      value: targetQuestion.isExcludeQuestionFromFinalScore ? 0 : optionValue,
      text,
      externalScores: undefined,
    };
  });
};

export const applyDependentModifications = (
  currentQuestion: QuestionItem,
  option: QuestionOptionItem,
  clonedSections: SavedSections,
  currentQuestionnaire: KycQuestionnaire,
  text?: string,
) => {
  if (!currentQuestion.dependents) return;

  currentQuestion.dependents.forEach((dependentData: DependentsConfigSchema) => {
    const { dependentQuestionId, typeOfModification } = dependentData;
    const dependentQuestionSection = getSectionByQuestionId(currentQuestionnaire, dependentQuestionId);
    const dependentQuestion = getQuestionById(dependentQuestionSection, dependentQuestionId);

    if (!dependentQuestionSection || !dependentQuestion || !option._id) {
      return;
    }

    const dependentQuestionValueMapping = updateQuestionValueMapping(dependentQuestion, currentQuestion._id, option.value!);
    const dependentQuestionTotalValue = calculateQuestionTotalValue(dependentQuestionValueMapping);
    const dependentQuestionSelectedOption = selectTargetQuestionOption(dependentQuestion, dependentQuestionValueMapping);

    if (!(dependentQuestionSection._id in clonedSections)) {
      clonedSections[dependentQuestionSection._id] = {
        questions: {},
      };
    }

    // ** Add a dependent question to the clonedSections
    if (typeOfModification === TypeOfModification.ADD) {
      clonedSections[dependentQuestionSection._id].questions[dependentQuestion._id] = {
        options: dependentQuestionSelectedOption ? [dependentQuestionSelectedOption?.optionId] : [],
        value: dependentQuestion.isExcludeQuestionFromFinalScore ? 0 : (dependentQuestionTotalValue ?? 0),
        text,
        externalScores: undefined,
      };
    }

    // ** Handle override
    if (dependentQuestion.overrideValue) {
      if (
        dependentQuestion.dependsOn &&
        (isOverrideOptionInClonedSection(clonedSections, dependentQuestion.dependsOn[0]) || option._id === dependentQuestion.dependsOn[0])
      ) {
        clonedSections[dependentQuestionSection._id].questions[dependentQuestion._id] = {
          options: [dependentQuestion.overrideValue],
          value: dependentQuestion.isExcludeQuestionFromFinalScore ? 0 : (dependentQuestionTotalValue ?? 0),
          text,
          externalScores: undefined,
        };
      }
    }
  });
};

export const saveKycOption =
  (sectionId: string, questionId: string, option: QuestionOptionItem, text?: string): Result<void> =>
  (dispatch, getState) => {
    const { kycQuestionnaire } = getState();
    const { savedSections } = kycQuestionnaire.newRiskProfile;
    const { currentQuestionnaire } = kycQuestionnaire;

    const currentSection = currentQuestionnaire?.sections.find(section => section._id === sectionId)!;
    const currentQuestion = currentSection.questions.find(question => question._id === questionId)!;
    const isMultiSelect = currentQuestion.type === QUESTION_TYPES.MULTIPLE_CHOICES_MULTI_SELECT;

    let clonedSections = deepClone(savedSections);

    if (!clonedSections) {
      clonedSections = {};
    }

    if (!(sectionId in clonedSections)) {
      clonedSections[sectionId] = {
        questions: {},
      };
    }

    let newOptions = [option._id];
    let newValue = option.value;
    let newExternalScores = option.externalScore ? [option.externalScore] : undefined;
    if (isMultiSelect) {
      const currentOptions: string[] | undefined = clonedSections[sectionId].questions[questionId]?.options;
      const currentValue: number | undefined = clonedSections[sectionId].questions[questionId]?.value;
      const currentExternalScores: { key: string; value: string }[] | undefined = clonedSections[sectionId].questions[questionId]?.externalScores;
      newOptions = currentOptions ? [...currentOptions] : [];
      newValue = currentValue || 0;
      newExternalScores = currentExternalScores ? [...currentExternalScores] : [];
      const index = newOptions.indexOf(option._id);
      if (index === -1) {
        newOptions.push(option._id);
        newValue += option.value;
        if (option.externalScore) {
          newExternalScores.push(option.externalScore);
        }
      } else if (newOptions.length > 1) {
        newOptions.splice(index, 1);
        newValue -= option.value;
        if (option.externalScore != null) {
          newExternalScores = newExternalScores.reduce((acc, externalScore) => {
            if (externalScore != null && !(externalScore?.key === option.externalScore?.key && externalScore?.value === option.externalScore?.value)) {
            }
            return acc;
          }, []);
        }
      }
    }

    const optionData = {
      options: newOptions,
      text,
      value: newValue,
      externalScores: newExternalScores,
    };

    const currentSectionData = currentQuestionnaire?.sections.find(section => section._id === sectionId);
    if (currentSectionData?.isExcludeSectionFromFinalScore) {
      clonedSections[sectionId].isExcludeSectionFromFinalScore = true;
    }

    clonedSections[sectionId].questions[questionId] = optionData;

    if (currentQuestion.dependents && currentQuestion.dependents.length > 0) {
      applyDependentModifications(currentQuestion, option, clonedSections, currentQuestionnaire!, text);
    }

    // Apply multiple overrides from the questionnaire configuration
    applyMultipleOverrides(clonedSections, currentQuestionnaire!, text);

    // Apply COMPARE modifications last to ensure they take precedence over simple overrides
    // This handles questions where multiple sources feed into one target
    // COMPARE will select the most conservative option among all applicable sources
    applyCompareModifications(clonedSections, currentQuestionnaire!, text);

    dispatch({ type: SET_RISK_PROFILE_SECTIONS.type, savedSections: clonedSections });
  };

export const checkKycOptionExists =
  (sectionId: string, questionId: string, option: string): Result<boolean> =>
  (dispatch, getState) => {
    const { savedSections } = getState().kycQuestionnaire.newRiskProfile;

    return (
      !!savedSections &&
      sectionId in savedSections &&
      questionId in savedSections[sectionId].questions &&
      savedSections[sectionId].questions[questionId].options.includes(option)
    );
  };

export const calculateKycQuestionnaireResult = (): Result<void> => async (dispatch, getState) => {
  const { currentQuestionnaire, newRiskProfile } = getState().kycQuestionnaire;
  const { savedSections } = newRiskProfile;
  let profile: RiskProfileItem | undefined;
  let score: number | undefined;

  if (currentQuestionnaire && savedSections) {
    const calculatedRiskProfile = await dispatch(calculateCorrelateRiskProfile(currentQuestionnaire, savedSections));
    profile = calculatedRiskProfile?.calculatedConfig ?? undefined;
    score = calculatedRiskProfile?.score ?? undefined;
  }

  dispatch({ type: SET_RECOMMENDED_RISK_PROFILE.type, profile });
  dispatch({ type: SET_RISK_PROFILE.type, profile });
  dispatch({ type: SET_RISK_PROFILE_SCORE.type, score });
};

export const setRecommendedRiskProfile =
  (riskProfile: SelectedRiskProfile): Result<void> =>
  async (dispatch, getState) => {
    const recommendedRiskProfileId = riskProfile?.upRisk?.recommendedRiskProfileId;
    if (recommendedRiskProfileId) {
      const { currentQuestionnaire } = getState().kycQuestionnaire;
      const profile = currentQuestionnaire?.riskProfiles.find(riskProfile => riskProfile._id === recommendedRiskProfileId);

      if (!profile) {
        throw new Error(`Risk profile with id ${recommendedRiskProfileId} not found in current questionnaire.`);
      }

      dispatch({ type: SET_RECOMMENDED_RISK_PROFILE.type, profile });
    }
  };

export const setRiskProfileScore =
  (score?: number): Result<void> =>
  dispatch => {
    dispatch({ type: SET_RISK_PROFILE_SCORE.type, score });
  };

export const setRiskProfile = (): Result<void> => (dispatch, getState) => {
  const { newRiskProfile, currentQuestionnaire } = getState().kycQuestionnaire;
  const { score } = newRiskProfile;

  const profile = calculateRiskProfile(score, currentQuestionnaire);

  dispatch({ type: SET_RISK_PROFILE.type, profile });
};

export const selectRiskProfile =
  (profile: RiskProfileItem): Result<void> =>
  dispatch => {
    dispatch({ type: SET_RISK_PROFILE.type, profile });
  };

export const getUnansweredKycQuestions =
  (section: QuestionnaireSectionItem): Result<QuestionItem[]> =>
  (dispatch, getState) => {
    const { savedSections } = getState().kycQuestionnaire.newRiskProfile;
    const sectionId = section._id;

    return section.questions.filter(question => !savedSections || !(sectionId in savedSections) || !(question._id in savedSections[sectionId].questions));
  };

export const getKycSectionSubtotal =
  (section: QuestionnaireSectionItem): Result<number> =>
  (dispatch, getState) => {
    const { savedSections } = getState().kycQuestionnaire.newRiskProfile;
    const sectionId = section._id;

    return section.questions.reduce((sum, question) => {
      if (!savedSections || !savedSections[sectionId] || !savedSections[sectionId].questions[question._id]) {
        return sum;
      }

      return sum + savedSections[sectionId].questions[question._id].value;
    }, 0);
  };

export const setRiskProfileForEdit =
  (profile: ContactRiskProfile): Result<void> =>
  (dispatch, getState) => {
    // Retrieve the questionnaire that was used to save the Contact Risk Profile
    // Extract all answers with their score value
    // Populate the saved Contact Risk Profile answers with the score
    const kycQuestionnaire = getState().kycQuestionnaire.questionnaires.find(questionnaire => questionnaire._id === profile.questionnaire);
    let newRiskProfileFromContact = profile;
    if (kycQuestionnaire) {
      const options = kycQuestionnaire.sections.flatMap(section => section.questions).flatMap(question => question.options);

      const sectionsWithOptionValues = Object.keys(profile.savedSections).reduce(
        (sections, section) => ({
          ...sections,
          [section]: {
            ...profile.savedSections[section],
            questions: Object.keys(profile.savedSections[section].questions).reduce((questions, question) => {
              const value: number = profile.savedSections[section].questions[question].options.reduce((acc, answer) => {
                const v: number = options.find(option => option._id === answer)?.value || 0;
                return acc + v;
              }, 0);

              return {
                ...questions,
                [question]: {
                  ...profile.savedSections[section].questions[question],
                  value,
                },
              };
            }, {} as SavedQuestions),
          },
        }),
        {} as SavedSections,
      );

      newRiskProfileFromContact = { ...profile, savedSections: sectionsWithOptionValues };
    }

    dispatch({ type: SET_RISK_PROFILE_FOR_EDIT.type, profile: newRiskProfileFromContact });
  };

export const completeKycQuestionnaire = (): Result<void> => dispatch => {
  dispatch(COMPLETE_QUESTIONNAIRE);
};

export const goBackKycQuestionnaire = (): Result<void> => dispatch => {
  dispatch(GO_BACK_QUESTIONNAIRE);
};
